import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, RotateCcw, Sparkles, Edit2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import api from '../lib/api';

const LANGUAGES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'pt', label: 'Português' },
];

const SCOPES = [
  { value: 'global', label_fr: 'Global (tous les utilisateurs)', label_en: 'Global (all users)', label_pt: 'Global (todos os utilizadores)' },
  { value: 'user', label_fr: 'Utilisateur spécifique', label_en: 'Specific user', label_pt: 'Utilizador específico' },
  { value: 'budget_type', label_fr: 'Type de budget', label_en: 'Budget type', label_pt: 'Tipo de orçamento' },
];

const BUDGET_TYPES = [
  { value: 'shared', label: 'Shared (familial partagé)' },
  { value: 'separate', label: 'Separate (séparé)' },
  { value: 'hybrid', label: 'Hybrid (hybride)' },
  { value: 'personal', label: 'Personal (personnel solo)' },
  { value: 'common', label: 'Common (pot commun)' },
];

export default function AdminAIPromptsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [variables, setVariables] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showVarsHelp, setShowVarsHelp] = useState(false);
  
  // Form state
  const [formName, setFormName] = useState('');
  const [formLanguage, setFormLanguage] = useState('fr');
  const [formScope, setFormScope] = useState('global');
  const [formTargetId, setFormTargetId] = useState('');
  const [formTemplate, setFormTemplate] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/ai-prompts');
      setPrompts(res.data.prompts || []);
      setVariables(res.data.variables || []);
    } catch (error) {
      console.error('Error loading prompts:', error);
      toast.error(language === 'fr' ? 'Erreur lors du chargement' : language === 'pt' ? 'Erro ao carregar' : 'Error loading');
    } finally {
      setLoading(false);
    }
  };

  const openNewEditor = () => {
    setEditingPrompt(null);
    setFormName('');
    setFormLanguage('fr');
    setFormScope('global');
    setFormTargetId('');
    setFormTemplate('');
    setFormActive(true);
    setShowEditor(true);
  };

  const openEditor = (prompt) => {
    setEditingPrompt(prompt);
    setFormName(prompt.name);
    setFormLanguage(prompt.language);
    setFormScope(prompt.scope);
    setFormTargetId(prompt.target_id || '');
    setFormTemplate(prompt.prompt_template);
    setFormActive(prompt.is_active);
    setShowEditor(true);
  };

  const loadDefaultTemplate = async () => {
    try {
      setLoadingDefault(true);
      const res = await api.get(`/admin/ai-prompts/defaults/${formLanguage}`);
      setFormTemplate(res.data.prompt_template);
      toast.success(language === 'fr' ? 'Modèle par défaut chargé' : language === 'pt' ? 'Modelo padrão carregado' : 'Default template loaded');
    } catch (error) {
      console.error('Error loading default:', error);
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setLoadingDefault(false);
    }
  };

  const handleSave = async () => {
    if (!formName.trim() || !formTemplate.trim()) {
      toast.error(language === 'fr' ? 'Nom et template requis' : 'Name and template required');
      return;
    }
    if ((formScope === 'user' || formScope === 'budget_type') && !formTargetId.trim()) {
      toast.error(language === 'fr' ? 'Cible requise pour ce scope' : 'Target required for this scope');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name: formName,
        language: formLanguage,
        scope: formScope,
        target_id: formScope === 'global' ? null : formTargetId,
        prompt_template: formTemplate,
        is_active: formActive,
      };
      
      if (editingPrompt) {
        await api.put(`/admin/ai-prompts/${editingPrompt.prompt_id}`, payload);
        toast.success(language === 'fr' ? 'Prompt mis à jour' : language === 'pt' ? 'Prompt atualizado' : 'Prompt updated');
      } else {
        await api.post('/admin/ai-prompts', payload);
        toast.success(language === 'fr' ? 'Prompt créé' : language === 'pt' ? 'Prompt criado' : 'Prompt created');
      }
      
      setShowEditor(false);
      loadPrompts();
    } catch (error) {
      console.error('Save error:', error);
      const detail = error.response?.data?.detail || (language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Save error');
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer ce prompt et revenir au défaut ?' : language === 'pt' ? 'Eliminar este prompt e voltar ao padrão?' : 'Delete this prompt and revert to default?')) return;
    try {
      await api.delete(`/admin/ai-prompts/${promptId}`);
      toast.success(language === 'fr' ? 'Prompt supprimé' : 'Prompt deleted');
      loadPrompts();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const handleToggleActive = async (prompt) => {
    try {
      await api.put(`/admin/ai-prompts/${prompt.prompt_id}`, { is_active: !prompt.is_active });
      loadPrompts();
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  const insertVar = (varName) => {
    const placeholder = `{${varName}}`;
    setFormTemplate((prev) => prev + placeholder);
  };

  const getScopeLabel = (scope) => {
    const s = SCOPES.find((x) => x.value === scope);
    return s ? (language === 'fr' ? s.label_fr : language === 'pt' ? s.label_pt : s.label_en) : scope;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-primary" />
                {language === 'fr' ? 'Prompts IA Simulateur' : language === 'pt' ? 'Prompts IA Simulador' : 'AI Simulator Prompts'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {language === 'fr' ? 'Gérer les prompts IA utilisés par le simulateur de dépenses' : language === 'pt' ? 'Gerir os prompts IA usados pelo simulador' : 'Manage AI prompts used by the expense simulator'}
              </p>
            </div>
          </div>
          <Button onClick={openNewEditor} data-testid="new-prompt-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Nouveau prompt' : language === 'pt' ? 'Novo prompt' : 'New prompt'}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {prompts.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium mb-2">
                {language === 'fr' ? 'Aucun prompt personnalisé' : language === 'pt' ? 'Nenhum prompt personalizado' : 'No custom prompt'}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {language === 'fr' ? 'Le simulateur utilise actuellement les prompts par défaut. Crée un prompt personnalisé pour le modifier.' : language === 'pt' ? 'O simulador usa atualmente os prompts padrão.' : 'The simulator currently uses the default prompts.'}
              </p>
              <Button onClick={openNewEditor} data-testid="create-first-prompt-btn">
                <Plus className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Créer le premier prompt' : language === 'pt' ? 'Criar primeiro prompt' : 'Create first prompt'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="prompts-list">
            {prompts.map((p) => (
              <Card key={p.prompt_id} data-testid={`prompt-card-${p.prompt_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold">{p.name}</h3>
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full uppercase">
                          {p.language}
                        </span>
                        <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
                          {getScopeLabel(p.scope)}
                        </span>
                        {p.target_id && (
                          <span className="text-xs bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full font-mono">
                            {p.target_id}
                          </span>
                        )}
                        {!p.is_active && (
                          <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded-full">
                            {language === 'fr' ? 'Inactif' : language === 'pt' ? 'Inativo' : 'Inactive'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 font-mono">
                        {p.prompt_template.substring(0, 200)}{p.prompt_template.length > 200 ? '...' : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleActive(p)}
                        title={p.is_active ? 'Désactiver' : 'Activer'}
                        data-testid={`toggle-active-${p.prompt_id}`}
                      >
                        {p.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditor(p)}
                        data-testid={`edit-prompt-${p.prompt_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(p.prompt_id)}
                        className="text-destructive"
                        data-testid={`delete-prompt-${p.prompt_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPrompt 
                ? (language === 'fr' ? 'Modifier le prompt' : language === 'pt' ? 'Editar prompt' : 'Edit prompt')
                : (language === 'fr' ? 'Nouveau prompt IA' : language === 'pt' ? 'Novo prompt IA' : 'New AI prompt')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium mb-1 block">
                {language === 'fr' ? 'Nom interne' : language === 'pt' ? 'Nome interno' : 'Internal name'}
              </label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder={language === 'fr' ? 'Ex: Prompt premium FR personnalisé' : 'Ex: Custom premium FR prompt'}
                data-testid="form-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {language === 'fr' ? 'Langue' : language === 'pt' ? 'Idioma' : 'Language'}
                </label>
                <Select value={formLanguage} onValueChange={setFormLanguage}>
                  <SelectTrigger data-testid="form-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {language === 'fr' ? 'Portée' : language === 'pt' ? 'Âmbito' : 'Scope'}
                </label>
                <Select value={formScope} onValueChange={(v) => { setFormScope(v); setFormTargetId(''); }}>
                  <SelectTrigger data-testid="form-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {language === 'fr' ? s.label_fr : language === 'pt' ? s.label_pt : s.label_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {formScope === 'user' && (
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {language === 'fr' ? 'ID Utilisateur (user_id)' : language === 'pt' ? 'ID Utilizador (user_id)' : 'User ID (user_id)'}
                </label>
                <Input
                  value={formTargetId}
                  onChange={(e) => setFormTargetId(e.target.value)}
                  placeholder="user_xxxxxxxx"
                  className="font-mono"
                  data-testid="form-target-user-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {language === 'fr' ? 'Tu peux trouver l\'ID dans Gestion Utilisateurs.' : language === 'pt' ? 'Encontra o ID em Gestão de Utilizadores.' : 'Find the ID in User Management.'}
                </p>
              </div>
            )}
            
            {formScope === 'budget_type' && (
              <div>
                <label className="text-sm font-medium mb-1 block">
                  {language === 'fr' ? 'Type de budget cible' : language === 'pt' ? 'Tipo de orçamento alvo' : 'Target budget type'}
                </label>
                <Select value={formTargetId} onValueChange={setFormTargetId}>
                  <SelectTrigger data-testid="form-target-budget-select">
                    <SelectValue placeholder={language === 'fr' ? 'Choisir...' : 'Choose...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGET_TYPES.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <Switch
                checked={formActive}
                onCheckedChange={setFormActive}
                data-testid="form-active-switch"
              />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {language === 'fr' ? 'Actif' : language === 'pt' ? 'Ativo' : 'Active'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Si désactivé, le simulateur retombera sur le prompt suivant dans la cascade.' : language === 'pt' ? 'Se desativado, o simulador usará o prompt seguinte na cascata.' : 'If disabled, the simulator will fall back to the next prompt in the cascade.'}
                </p>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">
                  {language === 'fr' ? 'Template du prompt' : language === 'pt' ? 'Template do prompt' : 'Prompt template'}
                </label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowVarsHelp(!showVarsHelp)}
                    data-testid="toggle-vars-help-btn"
                  >
                    {showVarsHelp ? (language === 'fr' ? 'Masquer variables' : 'Hide vars') : (language === 'fr' ? 'Variables disponibles' : 'Show vars')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadDefaultTemplate}
                    disabled={loadingDefault}
                    data-testid="load-default-btn"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    {language === 'fr' ? 'Charger défaut' : language === 'pt' ? 'Carregar padrão' : 'Load default'}
                  </Button>
                </div>
              </div>
              
              {showVarsHelp && (
                <div className="mb-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg max-h-48 overflow-y-auto">
                  <p className="text-xs font-medium text-blue-800 dark:text-blue-200 mb-2">
                    {language === 'fr' ? 'Cliquez sur une variable pour l\'insérer :' : language === 'pt' ? 'Clique numa variável para inserir:' : 'Click a variable to insert:'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {variables.map((v) => (
                      <button
                        key={v.name}
                        type="button"
                        onClick={() => insertVar(v.name)}
                        className="text-xs font-mono bg-white dark:bg-gray-800 px-2 py-1 rounded border hover:border-primary"
                        title={v.description_fr}
                        data-testid={`insert-var-${v.name}`}
                      >
                        {`{${v.name}}`}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <Textarea
                value={formTemplate}
                onChange={(e) => setFormTemplate(e.target.value)}
                className="font-mono text-xs min-h-[400px]"
                placeholder={language === 'fr' ? 'Tu es un conseiller financier...' : 'You are a financial advisor...'}
                data-testid="form-template-textarea"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {language === 'fr' ? `${formTemplate.length} caractères. Utilisez {variable} pour insérer des données dynamiques.` : `${formTemplate.length} characters. Use {variable} to insert dynamic data.`}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              {language === 'fr' ? 'Annuler' : language === 'pt' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-prompt-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? '...' : (language === 'fr' ? 'Enregistrer' : language === 'pt' ? 'Guardar' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
